import React, { useEffect, useMemo, useState } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import { Card, CardBody, Button } from "reactstrap"
import {
  FaChevronCircleLeft,
  FaChevronCircleRight,
  FaMapMarkerAlt,
} from "react-icons/fa"
import moment from "moment"
import dayjs from "dayjs"
import { DatePicker } from "antd"
import { fetchData } from "utils/Common_api_functions"
import { useDataRefresh } from "utils/useDataRefresh"
import Loader from "components/Loader/loader"
import { usePermissionData } from "AppContext"
import PermittedComponent from "PermittedComponent"
import "./holidayCalendar.css"

const { RangePicker } = DatePicker

const FILTERS = { DAY: "day", WEEK: "week", MONTH: "month" }

const HolidayCalendar = () => {
  const { checkAccess } = usePermissionData()

  const [loading, setLoading] = useState(false)
  const [holidays, setHolidays] = useState([])
  const [filterType, setFilterType] = useState(FILTERS.DAY)
  const [anchorDate, setAnchorDate] = useState(moment().startOf("day"))
  // Set when the manager picks dates directly in the range picker, overriding
  // the Day/Week/Month paging with an arbitrary custom range.
  const [customRange, setCustomRange] = useState(null)

  useEffect(() => {
    getAllHolidays()
  }, [])

  useDataRefresh(() => {
    getAllHolidays()
  })

  async function getAllHolidays() {
    const APIData = await fetchData(setLoading, "holiday")
    if (APIData?.success) {
      setHolidays(APIData?.data || [])
    }
  }

  // All holiday requests, expanded into a
  // date -> [{ name, location, badgeStatus }] lookup, tagged with their
  // status (Approved/Pending/Rejected/Reassigned) so each shows its own badge.
  const holidaysByDate = useMemo(() => {
    const map = {}
    holidays.forEach(h => {
      const from = moment(h.fromDate, "YYYY-MM-DD")
      const to = moment(h.toDate, "YYYY-MM-DD")
      if (!from.isValid() || !to.isValid()) return

      const name = h.employee?.name
      if (!name) return
      const location = h.events?.[0]?.location?.name || "—"
      const badgeStatus =
        h.status === true
          ? h.events?.[0]?.replaced === true
            ? "Reassigned"
            : "Approved"
          : h.status === false
          ? "Rejected"
          : "Pending"

      const cursor = from.clone()
      while (cursor.isSameOrBefore(to, "day")) {
        const key = cursor.format("YYYY-MM-DD")
        if (!map[key]) map[key] = []
        if (!map[key].some(e => e.name === name && e.location === location)) {
          map[key].push({ name, location, badgeStatus })
        }
        cursor.add(1, "day")
      }
    })
    return map
  }, [holidays])

  // Weeks (Sun-Sat, clipped to the month) that make up the anchored month.
  // Used both for the Month grid's columns and the Week-quick-jump pills.
  const monthWeeks = useMemo(() => {
    const monthStart = anchorDate.clone().startOf("month")
    const monthEnd = anchorDate.clone().endOf("month")
    const weeks = []
    let cursor = monthStart.clone()
    while (cursor.isSameOrBefore(monthEnd, "day")) {
      const weekEndOfWeek = cursor.clone().endOf("week")
      const weekEnd = moment.min(weekEndOfWeek, monthEnd)
      weeks.push({ start: cursor.clone(), end: weekEnd.clone() })
      cursor = weekEnd.clone().add(1, "day")
    }
    return weeks
  }, [anchorDate])

  const today = moment().startOf("day")

  const buildDayColumn = date => {
    const key = date.format("YYYY-MM-DD")
    const entries = (holidaysByDate[key] || []).map(e => ({ ...e }))
    return {
      key,
      label: date.format("dddd"),
      sub: date.format("DD/MM/YYYY"),
      isToday: date.isSame(today, "day"),
      entries,
    }
  }

  // Columns rendered in the grid: a day-column per day for Day/Week (or a
  // custom range), and Week 1/Week 2/... columns for Month.
  const columns = useMemo(() => {
    if (customRange && customRange.length === 2) {
      const start = moment(customRange[0].toDate()).startOf("day")
      const end = moment(customRange[1].toDate()).startOf("day")
      const cols = []
      const cursor = start.clone()
      while (cursor.isSameOrBefore(end, "day")) {
        cols.push(buildDayColumn(cursor.clone()))
        cursor.add(1, "day")
      }
      return cols
    }

    if (filterType === FILTERS.DAY) {
      return [buildDayColumn(anchorDate.clone())]
    }

    if (filterType === FILTERS.WEEK) {
      const weekStart = anchorDate.clone().startOf("week")
      return Array.from({ length: 7 }, (_, i) =>
        buildDayColumn(weekStart.clone().add(i, "day"))
      )
    }

    // MONTH — one column per week, entries tagged with their date since a
    // column now spans several days.
    return monthWeeks.map((w, idx) => {
      const entries = []
      const cursor = w.start.clone()
      while (cursor.isSameOrBefore(w.end, "day")) {
        const key = cursor.format("YYYY-MM-DD")
        ;(holidaysByDate[key] || []).forEach(e => {
          entries.push({ ...e, dateLabel: cursor.format("DD MMM") })
        })
        cursor.add(1, "day")
      }
      return {
        key: idx,
        label: `Week ${idx + 1}`,
        sub: `${w.start.format("DD MMM")} - ${w.end.format("DD MMM")}`,
        isToday: today.isBetween(w.start, w.end, "day", "[]"),
        entries,
      }
    })
  }, [customRange, filterType, anchorDate, monthWeeks, holidaysByDate])

  const selectFilter = f => {
    setFilterType(f)
    setCustomRange(null)
  }

  const goPrev = () => {
    if (customRange && customRange.length === 2) {
      const start = moment(customRange[0].toDate())
      const end = moment(customRange[1].toDate())
      const spanDays = end.diff(start, "day") + 1
      setCustomRange([
        dayjs(start.clone().subtract(spanDays, "day").toDate()),
        dayjs(end.clone().subtract(spanDays, "day").toDate()),
      ])
      return
    }
    if (filterType === FILTERS.DAY) {
      setAnchorDate(prev => prev.clone().subtract(1, "day"))
    } else if (filterType === FILTERS.WEEK) {
      setAnchorDate(prev => prev.clone().subtract(7, "day"))
    } else {
      setAnchorDate(prev => prev.clone().subtract(1, "month"))
    }
  }

  const goNext = () => {
    if (customRange && customRange.length === 2) {
      const start = moment(customRange[0].toDate())
      const end = moment(customRange[1].toDate())
      const spanDays = end.diff(start, "day") + 1
      setCustomRange([
        dayjs(start.clone().add(spanDays, "day").toDate()),
        dayjs(end.clone().add(spanDays, "day").toDate()),
      ])
      return
    }
    if (filterType === FILTERS.DAY) {
      setAnchorDate(prev => prev.clone().add(1, "day"))
    } else if (filterType === FILTERS.WEEK) {
      setAnchorDate(prev => prev.clone().add(7, "day"))
    } else {
      setAnchorDate(prev => prev.clone().add(1, "month"))
    }
  }

  // The range shown in the picker when no custom range has been picked
  const derivedRangeValue = useMemo(() => {
    if (!columns.length) return null
    if (filterType === FILTERS.MONTH && !customRange) {
      return [
        dayjs(monthWeeks[0]?.start.toDate()),
        dayjs(monthWeeks[monthWeeks.length - 1]?.end.toDate()),
      ]
    }
    const firstKey = columns[0]?.key
    const lastKey = columns[columns.length - 1]?.key
    if (typeof firstKey !== "string" || typeof lastKey !== "string") return null
    return [
      dayjs(moment(firstKey, "YYYY-MM-DD").toDate()),
      dayjs(moment(lastKey, "YYYY-MM-DD").toDate()),
    ]
  }, [columns, filterType, customRange, monthWeeks])

  const BADGE_CLASS = {
    Approved: "bg-success",
    Pending: "bg-warning text-dark",
    Reassigned: "bg-info text-dark",
    Rejected: "bg-danger",
  }

  const renderEntryCard = (entry, idx) => (
    <div
      key={`${entry.name}-${entry.location}-${idx}`}
      className={`holiday-shift-card holiday-shift-card--${entry.badgeStatus?.toLowerCase()}`}
      title={`${entry.name} - ${entry.location} (${entry.badgeStatus})`}
    >
      <span
        className={`badge ${BADGE_CLASS[entry.badgeStatus] || "bg-secondary"} holiday-shift-card__badge`}
      >
        {entry.badgeStatus}
      </span>
      {entry.dateLabel && (
        <div className="holiday-shift-card__date">{entry.dateLabel}</div>
      )}
      <div className="holiday-shift-card__name">{entry.name}</div>
      <div className="holiday-shift-card__location">
        <FaMapMarkerAlt size={9} className="me-1" />
        {entry.location}
      </div>
    </div>
  )

  return (
    <div className="page-content">
      <PermittedComponent isAccess={checkAccess(4, 1) || checkAccess(4, 2)}>
        {loading && <Loader />}
        <Breadcrumb
          title="Dashboard"
          breadcrumbItem="Holiday Calendar"
          description="See employee holiday requests — approved, pending, reassigned or rejected — for any day, week or month."
        />
        <Card className="shadow-lg">
          <CardBody>
            <div className="d-flex justify-content-between mb-3 flex-wrap gap-2 align-items-center">
              <div className="btn-group" role="group">
                {Object.values(FILTERS).map(f => (
                  <Button
                    key={f}
                    size="sm"
                    className="text-capitalize"
                    color={!customRange && filterType === f ? "primary" : "light"}
                    onClick={() => selectFilter(f)}
                  >
                    {f}
                  </Button>
                ))}
              </div>

              <div className="d-flex gap-2 align-items-center flex-wrap">
                <Button size="sm" onClick={goPrev}>
                  <FaChevronCircleLeft />
                </Button>

                <RangePicker
                  value={customRange || derivedRangeValue}
                  onChange={dates => {
                    if (dates && dates.length === 2) {
                      setCustomRange(dates)
                      setAnchorDate(moment(dates[0].toDate()).startOf("day"))
                    } else {
                      setCustomRange(null)
                    }
                  }}
                  format="DD/MM/YYYY"
                  allowClear
                />

                <Button size="sm" onClick={goNext}>
                  <FaChevronCircleRight />
                </Button>
              </div>
            </div>

            <div className="d-flex flex-column gap-3">
              {columns.map(col => (
                <div key={col.key} className="holiday-day-panel">
                  <div
                    className={`holiday-day-panel__header ${
                      col.isToday ? "holiday-col-today" : ""
                    }`}
                  >
                    <div className="holiday-day-panel__weekday">
                      {col.label}
                    </div>
                    <div className="holiday-day-panel__date">{col.sub}</div>
                  </div>
                  <div className="holiday-day-panel__body">
                    {col.entries.length ? (
                      <div className="d-flex flex-wrap gap-3">
                        {col.entries.map((entry, idx) => (
                          <div
                            key={`${entry.name}-${entry.location}-${idx}`}
                            style={{ width: 190 }}
                          >
                            {renderEntryCard(entry, idx)}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted" style={{ fontSize: 13 }}>
                        No Holidays
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      </PermittedComponent>
    </div>
  )
}

export default HolidayCalendar
